import IDouble from './IDouble';
import ISuiteNavLink from '../dataSources/suiteNav/ISuiteNavLink';
import ListTemplateType from '../dataSources/listCollection/ListTemplateType';
import { SiteReadOnlyState } from '../dataSources/site/SiteDataSource';
import { UnifiedHeaderLayout } from '../models/chrome/ChromeOptions';
import { SPVariantThemeType } from '../interfaces/theming/SPVariantThemeType';

/**
 * Context information that's available from _spPageContextInfo on page load and is applicable
 * for the life of the page (or at least as long as the same list is loaded).
 *
 * Instances should be thought of as READ-ONLY (possible exception: early in app boot).
 *
 * Potentially useful helper functions also defined in this file:
 * - getServerUrl() gets the server URL (http://server) from an ISpPageContext
 * - getSafeWebServerRelativeUrl() handles the case of the root web where the server sets
 *   webServerRelativeUrl to "/" (see method comments for more info)
 */
export interface ISpPageContext {
    // Please keep the individual fields alphabetically sorted within each group!

    /** AAD endpoint e.g. login.windows.net */
    aadInstanceUrl?: string;
    /** AAD tenant ID */
    aadTenantId?: string;
    /** AAD user ID */
    aadUserId?: string;
    /** OAuth token to make SharePoint calls (used in hosted scenarios, i.e. share, embed). */
    authToken?: string;
    /** Alternate url configured by the admin for self service site creation, rebased to the current zone */
    alternateWebAppHost?: string;
    /**
     * Whether downloading files should be blocked.
     * (If this is true, viewOnlyExperienceEnabled should also be true.)
     */
    blockDownloadsExperienceEnabled?: boolean;
    cdnPrefix?: string;
    /**
     * secrets used to do encryption of PersistedCache data
     */
    clientPersistedCacheKey?: IClientPersistedCacheKey;
    /** GUID correlation ID from the request for the aspx page. */
    CorrelationId?: string;
    /** Language ID, like 1033. */
    currentLanguage?: number;
    /** Like en-US. */
    currentCultureName?: string;
    /** Like en-US. */
    currentUICultureName: string;
    /** Locale ID, like 1033. */
    currentCultureLCID?: number;
    /** GUID of Design Package for the web */
    DesignPackageId?: string;
    /** Switch to turn app views off or on  */
    disableAppViews?: boolean;
    /** Switch to turn Microsoft Flow off or on  */
    disableFlows?: boolean;
    /** Info related to current drive */
    driveInfo?: {
        /** Access token for accessing the user's Document space using Vroom. */
        '.accessToken'?: string,
        /** Access token for the drive content using Vroom 2.0. */
        '.driveAccessToken'?: string,
        /** Access token for the drive content using Vroom 2.1. */
        '.driveAccessTokenV21'?: string,
        /** Drive URL for the list root. */
        '.driveUrl'?: string,
        /** Drive URL for the list root using Vroom 2.1. */
        '.driveUrlV21'?: string
    };
    /** Whether to allow download of malicious file */
    allowInfectedDownload?: boolean;
    /** Environment (like EDog; null for devbox). */
    env: string;
    /** Currently enabled flights, in a highly esoteric format... */
    ExpFeatures?: number[];
    /** Farm label, like US_16_Content. */
    farmLabel?: string;
    /** The form digest value used to validate postback requests. */
    formDigestValue?: string;
    /**
     * The time interval in seconds to the form digest expiration.
     * To be used in conjunction with the updateFormDigestPageLoaded to compute the actual expiration time.
     */
    formDigestTimeoutSeconds?: number;
    /** Return a valid Auth token */
    getAuthToken?: () => Promise<string | undefined>;
    /** Alias of the group. Null if not a group site. */
    groupAlias?: string;
    /** Color associated with the group or site, in #RRGGBB or #AARRGGBB format. */
    groupColor?: string;
    /** Whether the group has the conversation link in the quick launch by default (it's new enough or had an upgrader run). */
    groupHasQuickLaunchConversationsLink?: boolean;
    /** Indicates if the homepage has been provisioned for this group or not. */
    groupHasHomepage?: boolean;
    /** GUID of Group. If the site is not a Group Site, this property will be null. */
    groupId?: string;
    /** Type of group: Public or Private (use GroupType string enum). Null if not a group site. */
    groupType?: string;
    /** Whether guest sharing is enabled (on the site level - might still be disabled globally at the tenant level). */
    guestsEnabled?: boolean;
    /** If true, a site design run may be pending on this web. */
    hasPendingWebTemplateExtension?: boolean;
    /** The emphasis of the header, i.e. which variant theme it uses */
    headerEmphasis?: SPVariantThemeType;
    /** The layout template of the header */
    headerLayout?: UnifiedHeaderLayout;
    /** True if the sync button should be hidden in ODB. */
    hideSyncButtonOnODB?: boolean;
    /** GUID of the hub site this site belongs to. */
    hubSiteId?: string;
    /** True if the idle session sign out policy is enabled for this request */
    idleSessionSignOutEnabled?: boolean;
    /** True if this is a hub site ("parent" providing shared nav/etc for other sites) */
    isHubSite?: boolean;
    /** Has NoScript enabled on the site, which prevents site admins from running abritrary script on the site to prevent XSS attacks. */
    isNoScriptEnabled?: boolean;
    /** Whether or not users may create their own sites within the tenant. */
    isSelfServiceSiteCreationEnabled?: boolean;
    /** Whether this server is SharePoint Online (true for SPO, false for on-premises). */
    isSPO?: boolean;
    /** Whether this tenant has a developer license */
    isTenantDevSite?: boolean;
    /** Mapping containing GUIDs of kill switches that are currently on */
    killSwitches?: { [guid: string]: boolean };
    /** Probably "_layouts/15". */
    layoutsUrl: string;
    /** max file size for upload. */
    maximumFileSize?: number;
    /** Contains settings menu items. */
    MenuData?: { SettingsData: ISuiteNavLink[] };
    /** Whether or not modern theming is enabled. Is calculated based on flights and features on the server. */
    modernThemingEnabled?: boolean;
    /** MS Graph endpoint for the current environment, e.g. https://graph.microsoft.com */
    msGraphEndpointUrl?: string;

    /**
     * @field
     * @name msMruEndpointUrl
     * @description Most-Recently Used (MRU) API endpoint for the current environment.
     * @example https://ocws.officeapps.live.com
     */
    msMruEndpointUrl?: string;

    /** Top navigation and quick launch nodes. */
    navigationInfo?: { quickLaunch: INavNode[], topNav: INavNode[] };
    /** True if the list items should open in client apps (vs. Office Online). */
    openInClient?: boolean;
    /** The site page item id, -1 if current page is not a site page. */
    pageItemId: number;
    /** True if user configured to use user regional settings */
    preferUserTimeZone: boolean;
    /** Indicates whether the preview features switch is enabled at the Tenant level */
    PreviewFeaturesEnabled?: boolean;
    /** Indicates whether the publishing features switch is enabled on the site */
    PublishingFeatureOn?: boolean;
    /** Read-only state for the site. */
    readOnlyState?: SiteReadOnlyState;
    /** For recycle bin page only, number of items in the recycle bin. On other pages it will be -1. */
    RecycleBinItemCount?: number;
    /** Whether content in the site is restricted to region. */
    restrictedToRegion?: number;
    /** Overrides the search box search scope of the current site */
    searchScope?: SearchScopeType;
    /** The classification label id */
    sensitivityLabel?: string;
    /** Server redirected page request url. */
    serverRedirectedUrl?: string;
    /** The site-relative URL of the initial request. */
    serverRequestPath: string;
    /** Server time when page was pushed to client. */
    serverTime?: string;
    /** True if the NGSC dialog should be shown in ODB. */
    showNGSCDialogForSyncOnODB?: boolean;
    /** True if the NGSC dialog should be shown in TeamSite. */
    showNGSCDialogForSyncOnTS?: boolean;
    /**
     * Absolute URL of the current site (collection), like "https://microsoft.sharepoint.com/teams/odsp".
     * Should not have a trailing slash, will not be encoded, and could contain spaces. Note that while in many cases
     * this will match the webAbsoluteUrl (since there usually exists a web at the root of the current site collection),
     * this will not always be true:
     *
     * If the current page is, e.g. "https://microsoft.sharepoint.com/teams/odsp/design/SitePages/Home.aspx",
     * then the siteAbsoluteUrl will be "https://microsoft.sharepoint.com/teams/odsp"
     * and the webAbsoluteUrl will be "https://microsoft.sharepoint.com/teams/odsp/design".
     *
     * Most scenarios should use webAbsoluteUrl for the root of the current web, rather than the site (collection).
     */
    siteAbsoluteUrl: string;
    /** Server-relative URL of the site, like "/sites/odsp" or "/" for the root web */
    siteServerRelativeUrl?: string;
    /** Site classification e.g. (MBI). */
    siteClassification?: string;
    /** Like "4$$16.0.4524.1200". Part before $$ is used to tell if theme has been updated. */
    siteClientTag: string;
    siteExternalSharingEnabled?: boolean;
    /** GUID of the current site. */
    siteId?: string;
    /** Site Pages feature (WEX) enabled on the site. */
    sitePagesEnabled?: boolean;
    /* ID of the tenant (GUID); used for telemetry. */
    siteSubscriptionId?: string;
    /* Whether substrate for ODB is disabled */
    substrateOneDriveDisabled?: boolean;
    /** boolean flag to indicate if the server supports # in file/folder name. */
    supportPoundStorePath?: boolean;
    /** boolean flag to indicate if the server supports % in file/folder name. */
    supportPercentStorePath?: boolean;
    /** Cache token for the current web theme. */
    themeCacheToken?: string;
    /** URL of the current web theme. */
    themedCssFolderUrl?: string;
    /** Date request digest was last loaded. Probably only relevant to classic pages. */
    updateFormDigestPageLoaded?: Date;
    /** boolean flag to indicate whether to display the Feedback button pointing to UserVoice */
    userVoiceForFeedbackEnabled?: boolean;
    /**
     * If true, force viewing in browser for handled file types (e.g. Office docs), but
     * allow download of files that can't be viewed in the browser.
     */
    viewOnlyExperienceEnabled?: boolean;
    /**
     * Absolute URL of the current web, like "https://microsoft.sharepoint.com/teams/odsp/design".
     * Should not have a trailing slash, will not be encoded, and could contain spaces. Note that while in many cases
     * this will match the siteAbsoluteUrl (since there usually exists a web at the root of the current site collection),
     * this will not always be true:
     *
     * If the current page is, e.g. "https://microsoft.sharepoint.com/teams/odsp/design/SitePages/Home.aspx",
     * then the siteAbsoluteUrl will be "https://microsoft.sharepoint.com/teams/odsp"
     * and the webAbsoluteUrl will be "https://microsoft.sharepoint.com/teams/odsp/design".
     *
     * In most scenarios, this is the url to use as the current root.
     */
    webAbsoluteUrl: string;
    /** Description of the current web. */
    webDescription?: string;
    /** First day of week setting of the current web. 0-6 map to Sunday-Saturday. Null if not configured or no access to this setting.*/
    webFirstDayOfWeek?: number;
    /** GUID of the current web. */
    webId?: string;
    /** Language ID in which the web was created, like 1033 (which does not change) */
    webLanguage?: number;
    /** URL to the web logo (defaults to /_layouts/15/images/siteicon.png). */
    webLogoUrl: string;
    /** Web property bit flags including chrome options, the bit mapping are defined in enum SPWebPropertyFlags2 */
    webPropertyFlags2?: number;
    /**
     * DO NOT USE DIRECTLY!
     * Server-relative URL of the web, like "/sites/odsp", or "/" for the root web.
     *
     * IMPORTANT: When building URLs, use getSafeWebServerRelativeUrl(pageContext) from this file
     * to handle the root web "/" case properly.
     *
     * Should not have a trailing slash (except in the case of the root web), will not be encoded,
     * and could contain spaces.
     */
    webServerRelativeUrl: string;
    /**
     * The web's template id. See the enum interfaces/WebTemplateType for possible values.
     *
     * @example To compare against WebTemplateType enums, convert webTemplate to a number:
     *     Number(pageContext.webTemplate) === WebTemplateType.teamSite
     */
    webTemplate: string;
    /**
     * The web's template configuration. Combines the webTemplate with it's type.
     * So modern team site is STS#3 as it's the 3rd iteration of the teamsite template.
     */
    webTemplateConfiguration?: string;
    /** Display name of the web. */
    webTitle: string;
    /** Time zone setting of the current web. Null if not configured or no permission to access. */
    webTimeZoneData?: ISPSystemTimeZoneData;
    // Please add your field in alphabetical order, not here!

    //////////////// User info ////////////////
    /** True if the user is entitled to create Microsoft Forms */
    canUserCreateMicrosoftForm?: boolean;
    /** True if the user has the manage web permission level. */
    hasManageWebPermissions: boolean;
    /** True when the user is anonymous guest. */
    isAnonymousGuestUser?: boolean;
    /** True if current SPWeb is the container web for an SPApp. */
    isAppWeb: boolean;
    /** True when the user is email authentication guest. */
    isEmailAuthenticationGuestUser?: boolean;
    /** True when the user is external guest */
    isExternalGuestUser?: boolean;
    /** Whether the user is the admin of the site. */
    isSiteAdmin: boolean;
    /** User identifier like "i:0h.f|membership|100300008aff61c1@live.com". */
    systemUserKey: string;
    /** User display name like "Jon Doe". */
    userDisplayName: string;
    /** First day of week setting of the current user. 0-6 map to Sunday-Saturday. Null if not configured or no access to this setting.*/
    userFirstDayOfWeek?: number;
    userId: number;
    /** User login name, like "user@microsoft.com". */
    userLoginName: string;
    /** User email address, like "user@microsoft.com". */
    userEmail: string;
    /** The user's permission mask for the current web. Use with PermissionMask utility functions. */
    webPermMasks?: IDouble;
    /** Timezone setting of the current user. Null if not configured or no permission to access. */
    userTimeZoneData?: ISPSystemTimeZoneData;

    ///////////////// List level settings from SPContext.Current.List /////////////
    // Most of these won't be set in ODBNext, since server-side onedrive.aspx is outside of a
    // list context and thus SPContext.Current.List is always null. These should only be used in
    // lists/doclibs or other aspx pages where SPContext.Current.List is available server-side.
    /** List template from SPContext.Current.List, if any. -1 if unknown. */
    listBaseTemplate?: ListTemplateType;
    /** GUID from SPContext.Current.List, if any. */
    listId?: string;
    /** The user's permission mask for SPContext.Current.List. Use with PermissionMask utility functions. */
    listPermsMask?: IDouble;
    /** The display name of the list from SPContext.Current.List. */
    listTitle?: string;
    /** The default list URL for this app. On ODB, this is manually set in ODBOneDrive.ts. */
    listUrl?: string;
    /** The GUID of the list view associated with this aspx page, from SPContext.Current.List. */
    viewId?: string;

    // Please add your field in alphabetical order, not here!
}

/**
 * Interface for a navigation node.
 *
 * Note that the properties here reflect the structure of the JSON blob returned by the server,
 * at the time of writing.
 */
export interface INavNode {
    Id: number;
    Title: string;
    Url: string;
    IsDocLib: boolean;
    IsExternal: boolean;
    ParentId: number;
    Children: INavNode[];
    ListTemplateType?: ListTemplateType;
}

export interface IPersistedCacheKey {
    Key: string;
    Date: string;
}

export interface IClientPersistedCacheKey {
    CurrentKey: IPersistedCacheKey;
    PreviousKey: IPersistedCacheKey;
}

/**
 * Date time without regional info.
 */
export interface ISPSystemTime {
    Day: number;
    DayOfWeek: number;
    Hour: number;
    Milliseconds: number;
    Minute: number;
    Month: number;
    Second: number;
    Year: number;
}

/**
 * SharePoint time zone data.
 */
export interface ISPSystemTimeZoneData {
    DaylightDate: ISPSystemTime;
    DaylightBias: number;
    Description: string;
    Id: number;
    Bias: number;
    StandardDate: ISPSystemTime;
    StandardBias: number;
}

/**
 * Search box search scope
 * When the value is Default, it follows the existing search scope rule.
 * For example, hub site has hub scope, non-hub site has site scope.
 */
export const enum SearchScopeType {
    Default = 0,
    Tenant = 1,
    Hub = 2,
    Site = 3
}

/**
 * Web property flags enum
 * property name matchs SPWebPropertyFlags2 definition in sts/stsom/SPWeb.cs
 */
export enum SPWebPropertyFlags2 {
    /** Indicates whether site footer is enabled in this web */
    FooterEnabled = 0x0000000000000002,
    /** Indicates whether mega menu is enabled on this web */
    MegaMenuEnabled = 0x0000000000000080
}

/** Default site logo URL */
export const DEFAULT_LOGO_URL: string = '_layouts/15/images/siteicon.png';

/**
 * Gets the absolute URL of the server (like https://microsoft.sharepoint.com).
 * Input can be an ISpPageContext or other object containing absolute and server-relative web URLs.
 *
 * @example
 *  Returns "https://microsoft.sharepoint.com"
 *      getServerUrl({
 *          webAbsoluteUrl: "https://microsoft.sharepoint.com/sites/odsp",
 *          webServerRelativeUrl: "/sites/odsp"
 *      })
 *
 * Returns "http://server"
 *     getServerUrl({
 *          webAbsoluteUrl: "http://server",
 *          webServerRelativeUrl: "/"
 *     })
 */
export function getServerUrl(pageContext: {
    webAbsoluteUrl: string;
    webServerRelativeUrl: string
}): string {
    'use strict';

    let { webAbsoluteUrl, webServerRelativeUrl } = pageContext;

    // Handle cases like http://server site collection, where webServerRelativeUrl will be '/'
    return webServerRelativeUrl === '/' || webServerRelativeUrl === ''
        ? webAbsoluteUrl
        : webAbsoluteUrl.substring(0, webAbsoluteUrl.lastIndexOf(webServerRelativeUrl));
}

/**
 * In most cases, returns the webServerRelativeUrl as-is.
 * For the root web (http://server), returns ''.
 *
 * This function is needed because the root web's webServerRelativeUrl will be '/',
 * which is usually not desirable when combining with other paths. For example:
 *    Good:  '/myweb' + '/_api/contextinfo' => '/myweb/_api/contextinfo'
 *    Bad:   '/'      + '/_api/contextinfo' => '//_api/contextinfo'
 *    Fixed: ''       + '/_api/contextinfo' => '/_api/contextinfo'
 *
 * @param pageContext - An ISpPageContext or other object containing the server-relative URL of a web
 */
export function getSafeWebServerRelativeUrl(pageContext: {
    webServerRelativeUrl: string;
}): string {
    'use strict';

    return pageContext.webServerRelativeUrl === '/' ? '' : pageContext.webServerRelativeUrl;
}

/**
 * Returns true if the current web is the root of a site collection associated with a group.
 * Otherwise it returns false.
 */
export function isGroupWebContext(pageContext: {
    groupId?: string,
    webAbsoluteUrl: string,
    siteAbsoluteUrl: string
}): boolean {
    'use strict';

    return pageContext && !!pageContext.groupId &&
        pageContext.webAbsoluteUrl === pageContext.siteAbsoluteUrl;
}

/**
 * Returns true if the current web templates is one of the modern templates: STS#3, GROUP#0, SITEPAGEPUBLISHING#0.
 */
export function isModernTemplate(pageContext: {
    webTemplateConfiguration: string
}): boolean {
    const modernTemplates: string[] = ['STS#3', 'GROUP#0', 'SITEPAGEPUBLISHING#0'];

    return modernTemplates.indexOf(pageContext.webTemplateConfiguration) !== -1;
}

export { ISuiteNavLink };
export default ISpPageContext;
